import Big from "big.js";
import { Type } from "../../helper";
import { calculateItemRow } from "../../calculation/calculateItemRow";
import { select } from "redux-saga/effects";
import { calculateBundleRow } from "../../calculation/calculateBundleRow";
import { calculateSummary } from "../../calculation/calculateSummary";
import { RowType } from "../../../../components/Editable/FormItem/Rows/RowType";
/**
 * Add in apply_amount of local storage storageLinkItems into fetchedLinkItems.
 * Here, we also add inapplicable transaction numbers into tooMuchAppliedArr,
 * or add transactions which apply_amount is updated into updatedLinkedItemAppliedArr.
 * @param {*} transactionType transaction type. Some transaction types will have special handle cases.
 * @param {*} fetchedLinkItems fethed link items from api
 * @param {*} storageLinkItems autosave-stored link items
 * @param {*} tooMuchAppliedArr array to put txns with balance < stored apply amount
 * Below 2 are only passed in if is: invoices/bills/sales CN/purchase CN/deemed payment.
 * @param {*} updatedLinkedItemAppliedArr An array to put txns that we'll make changes to its apply_amount
 * @param {*} transaction the txn object.
 * @returns new set of link items to be used in redux, until the next api call (eg on contact change)
 */
export function* getNewLinkItemsWithApplyAmount(
    transactionType, fetchedLinkItems, storageLinkItems, tooMuchAppliedArr,
    updatedLinkedItemAppliedArr = null, transaction = {},
) {
    const taxCodes = yield select(state => state.List.taxCodes);

    const isInvoiceOrBill = [Type.sale_invoice, Type.purchase_bill].includes(transactionType);

    // See if stored balance is different with the fetched linked item's balance;
    if (updatedLinkedItemAppliedArr && isInvoiceOrBill) {
        let summaryBalanceB = new Big(transaction.form_summary_total || 0);
        let prevSummaryBalanceB = new Big(transaction.form_summary_total || 0);

        const newTransactionLinkItems = [];
        const alreadyAddedArr = []; // ids of transactions that we already add into newTransactionLinkItems
        let hasAnyChange = false;

        // Loop through stored linked items first, then only add in remaining linked items,
        // to preserve the order of selection
        storageLinkItems.forEach(storedLinkItem => {
            const fetchedLinkItem = fetchedLinkItems.find(
                i => i.target_transaction_id === storedLinkItem.target_transaction_id,
            );

            // as long as one linked item has changes, the subsequent ones would most probably have changes in applied amount,
            // despite their stored and fetched balance being the same or not
            if (fetchedLinkItem &&
                (!new Big(storedLinkItem.balance).eq(new Big(fetchedLinkItem.balance)) || hasAnyChange)) {
                let toApplyB;

                // calculate previously stored version's applied amount. To be used to show the before and after amount in modal
                const prevApplyB = new Big(Math.min(prevSummaryBalanceB.toNumber(), storedLinkItem.balance));

                // if no more balance to be knocked off, set the apply amount to be 0
                if (summaryBalanceB.lte(0)) {
                    toApplyB = new Big(0);
                }
                else {
                    toApplyB = new Big(Math.min(summaryBalanceB.toNumber(), fetchedLinkItem.balance));
                    summaryBalanceB = summaryBalanceB.minus(toApplyB);

                    alreadyAddedArr.push(storedLinkItem.target_transaction_id);
                    newTransactionLinkItems.push({...fetchedLinkItem});
                }

                // modal should only show transactions that have changes in applied amount
                if (!prevApplyB.eq(toApplyB)) {
                    updatedLinkedItemAppliedArr.push({
                        id: storedLinkItem.target_transaction_id,
                        number: storedLinkItem.number,
                        // stored: storedLinkItem.balance,
                        // current: fetchedLinkItem.balance,
                        prevApply: prevApplyB.toNumber(),
                        toApply: toApplyB.toNumber(),
                    });
                }

                hasAnyChange = true;
                prevSummaryBalanceB = prevSummaryBalanceB.minus(prevApplyB);
            }
        });

        // add in remaining linked items
        const newFetchedLinkItemsFiltered = fetchedLinkItems.filter(
            i => !alreadyAddedArr.includes(i.target_transaction_id));

        return [...newTransactionLinkItems, ...newFetchedLinkItemsFiltered];
    }

    // This part is for sales/purchase CN. To see if stored form items total (transaction.form_summary_total)
    // is different with post-checking form items total (newFormItemsSummary.balance).
    // One example it may be different is due to subscription removed items.
    let hasTotalAmountChange, newFormItemsSummary, summaryBalanceB;
    if ([Type.sale_credit_note, Type.purchase_credit_note].includes(transactionType)) {
        const formItemsWithCalcData = transaction.form_items.map(record => {
            switch (record.type) {
                case RowType.subtotal:
                case RowType.subtitle:
                    return record;
                case RowType.bundle:
                    return calculateBundleRow(record, transaction.tax_mode, taxCodes);
                case RowType.item:
                default:
                    return calculateItemRow(record, transaction.tax_mode, taxCodes);
            }
        });
        const {
            summary,
        } = calculateSummary(formItemsWithCalcData, [], transaction.tax_mode, transaction.rounding_on);

        newFormItemsSummary = summary;
        hasTotalAmountChange = newFormItemsSummary?.balance !== transaction?.form_summary_total;

        summaryBalanceB = new Big(newFormItemsSummary?.balance || 0);
    }

    const newLinkItems = fetchedLinkItems.map(linkItem => {
        const newItem = {...linkItem};
        const storageItem = storageLinkItems.find(
            i => i.target_transaction_id === linkItem.target_transaction_id,
        );

        if (storageItem) {
            // if apply_amount > balance, don't use
            if (storageItem.apply_amount &&
                new Big(storageItem.apply_amount || 0).gt(new Big(newItem.balance || 0))
            ) {
                tooMuchAppliedArr.push({
                    id: storageItem.target_transaction_id,
                    number: storageItem.number,
                    prevApply: storageItem.apply_amount,
                    fetchedBalance: newItem.balance,
                });
            }
            // Tax deemed payment: have to set apply_amount here,
            // so that DeemedPayments/panelLinks.js updateLinkItemKeys() can use the values
            else if (transactionType === Type.tax_deemed_payment) {
                if (!storageItem.apply_amount) {
                    newItem.apply_amount = undefined;
                }
                else {
                    const prevApplyB = new Big(storageItem.apply_amount || 0);
                    // deemed payment apply_amount is non-editable, so should always follow linkItem.balance
                    const toApplyB = new Big(linkItem.balance || 0);

                    newItem.apply_amount = toApplyB.toNumber() || undefined;

                    if (!toApplyB.eq(prevApplyB)) {
                        // update the apply amount, and push into arr to show the changes in the modal
                        // newItem.apply_amount = toApplyB.toNumber();
                        updatedLinkedItemAppliedArr.push({
                            id: storageItem.target_transaction_id,
                            number: storageItem.number,
                            prevApply: prevApplyB.toNumber(),
                            toApply: toApplyB.toNumber(),
                        });
                    }
                }
            }
            // CN: if apply_amount is < balance, and there's a change in form items total, check the apply amounts
            else if (hasTotalAmountChange &&
                [Type.sale_credit_note, Type.purchase_credit_note].includes(transactionType)) {
                const prevApplyB = new Big(storageItem.apply_amount || 0);

                const toApplyB = new Big(Math.min(prevApplyB.toNumber(), summaryBalanceB.toNumber()));

                if (!toApplyB.eq(prevApplyB)) {
                    // update the apply amount, and push into arr to show the changes in the modal
                    newItem.apply_amount = toApplyB.toNumber();
                    updatedLinkedItemAppliedArr.push({
                        id: storageItem.target_transaction_id,
                        number: storageItem.number,
                        prevApply: prevApplyB.toNumber(),
                        toApply: toApplyB.toNumber(),
                    });
                }
                else {
                    newItem.apply_amount = toApplyB.toNumber() || undefined;
                }

                summaryBalanceB = summaryBalanceB.minus(toApplyB);
                // don't let it be negative
                summaryBalanceB = new Big(Math.max(0, summaryBalanceB.toNumber()));
            }
            else {
                newItem.apply_amount = storageItem.apply_amount || undefined;
            }
        }
        return newItem;
    });

    return newLinkItems;
}
